import actions from '../actionTypes';
import {
  SIZE_RANGE_PER_APPS,
  IMPERIAL_KEY,
  STRETCHED_BANNER_KEY,
  HANGING_BANNER_KEY,
  SQ_INCHES_TO_SQ_FEET,
  METRIC_KEY,
  SQ_FT_TO_SQ_METER,
  WALL_MINIMUM_TOTAL_WIDTH,
  MAX_CANVAS_WALL_SIZE,
  WALL_MINIMUM_TOTAL_HEIGHT,
  WALL_MAXIMUM_TOTAL_HEIGHT,
  CANVAS_CONSTRAINT_TILE_TYPE,
  CANVAS_FREESTYLE_TILE_TYPE,
  SIZE_SELECTION_PAGE,
  DESIGN_PAGE,
  DESIGN_SHARE_PAGE
} from '../../utilities/constants/Constants';
import {
  getArea,
  isMultiCanvas,
  convertImperialToGivenUnitSystem
} from '../../utilities/HelperFunctions/HelperFunctions';
import {
  selectPresetWall,
  sizeDropdownIsChanged,
  valueInImperial,
  getUnit,
  getActiveWall,
  checkIfCanvasApp,
  updateCanvasForChangeInSize,
  updateUIForProjectSize,
  settingsWallChange
} from '../homeActions';
import { Icons } from '../../utilities';
import { string } from '../../localization/Localization';
import {
  addToUndoManager,
  minimumViewingDistanceIsUpdated
} from '../fabricCanvasActions';
import { setCurrentPageIndex } from '../pageControllerActions';

export const getIconUrlBasedOnOrientationForSizeItem = size => (
  dispatch,
  getState
) => {
  const { iconUrl } = getState().modal.productSizes.default;
  if (dispatch(isMultiCanvas())) {
    return Icons.multiCanvasDesigner;
  }
  if (size.width > size.height) {
    return iconUrl.designerLandscapeIconUrl;
  }
  if (size.width < size.height) {
    return iconUrl.designerPortraitIconUrl;
  }
  return iconUrl.productIconUrl;
};

export const getIconUrlBasedOnRangeForSizeItem = size => (
  dispatch,
  getState
) => {
  const { unitSystem, iconUrl } = getState().modal.productSizes.default;
  const { medium, large } = dispatch(getMediumAndLargeSizeRange());
  const area = getArea(size.width, size.height, unitSystem);
  const mediumArea = getArea(medium.width, medium.height);
  const largeArea = getArea(large.width, large.height);
  if (area < mediumArea) {
    return iconUrl.productIconUrl;
  }
  if (area < largeArea || area >= mediumArea) {
    return iconUrl.designerLandscapeIconUrl;
  }
  return iconUrl.designerPortraitIconUrl;
};

export const getMediumAndLargeSizeRange = () => (dispatch, getState) => {
  const { selectedProduct } = getState().home;
  switch (selectedProduct.category) {
    case STRETCHED_BANNER_KEY:
      return {
        medium: SIZE_RANGE_PER_APPS.SBMediumSize,
        large: SIZE_RANGE_PER_APPS.SBLargeSize
      };
    case HANGING_BANNER_KEY:
      return {
        medium: SIZE_RANGE_PER_APPS.HBMediumSize,
        large: SIZE_RANGE_PER_APPS.HBLargeSize
      };
    default:
      return {
        medium: 0,
        large: 0
      };
  }
};

export const unitConverter = (itm, type, unitSystem) => {
  if (type === 'width') {
    if (unitSystem === IMPERIAL_KEY) {
      return itm.width;
    }
    return itm.widthMetric;
  }
  if (type === 'height') {
    if (unitSystem === IMPERIAL_KEY) {
      return itm.height;
    }
    return itm.heightMetric;
  }
  return null;
};

export const handleProjectSizeSelection = index => dispatch => {
  dispatch(sizeDropdownIsChanged(index));
  dispatch(selectPresetWall());
};

export const getAreaInGivenUnitSystem = (val, unit) => {
  const areaInSqFt = val / SQ_INCHES_TO_SQ_FEET;
  if (unit === METRIC_KEY) {
    return (areaInSqFt / SQ_FT_TO_SQ_METER).toFixed(4);
  }
  return areaInSqFt;
};

export const customWidthChange = inValue => (dispatch, getState) => {
  if (!getState().modal.productSizes.custom.sizes) return;

  const { unitSystem } = getState().home;
  const stringToFloatValue = parseFloat(inValue);
  const {
    minCustomWidth,
    maxCustomWidth
  } = getState().modal.productSizes.custom.sizes[unitSystem];
  const unit = unitSystem == METRIC_KEY ? 'cm' : 'in';

  let widthValid = '';
  let errorMessage = '';
  if (Number.isNaN(stringToFloatValue) || !stringToFloatValue) {
    widthValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
  } else if (stringToFloatValue < minCustomWidth) {
    widthValid = 'is-invalid';
    errorMessage = string.formatString(
      string.MINIMUM_WIDTH_KEY,
      minCustomWidth + unit
    );
  } else if (stringToFloatValue > maxCustomWidth) {
    widthValid = 'is-invalid';
    errorMessage = string.formatString(
      string.MAXIMUM_WIDTH_KEY,
      maxCustomWidth + unit
    );
  }
  const widthInImperial = valueInImperial(inValue, unitSystem);
  dispatch({
    type: actions.SELECT_CUSTOM_WALL_WIDTH,
    payload: {
      value: widthInImperial,
      widthInput: widthInImperial,
      widthValid,
      widthErrorMessage: errorMessage
    }
  });
  dispatch({
    type: actions.CUSTOM_WALL_ERROR,
    payload: { errorMessage }
  });
  if (widthValid == '') {
    dispatch(validateWidthSizeAttributesAndContinue());
  }
};

export const customHeightChange = inValue => (dispatch, getState) => {
  if (!getState().modal.productSizes.custom.sizes) return;

  const { unitSystem } = getState().home;
  const stringToFloatValue = parseFloat(inValue);
  const {
    minCustomHeight,
    maxCustomHeight
  } = getState().modal.productSizes.custom.sizes[unitSystem];
  const unit = unitSystem == METRIC_KEY ? 'cm' : 'in';

  let heightValid = '';
  let errorMessage = '';
  if (Number.isNaN(stringToFloatValue) || !stringToFloatValue) {
    heightValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
  } else if (stringToFloatValue < minCustomHeight) {
    heightValid = 'is-invalid';
    errorMessage = string.formatString(
      string.MINIMUM_HEIGHT_KEY,
      minCustomHeight + unit
    );
  } else if (stringToFloatValue > maxCustomHeight) {
    heightValid = 'is-invalid';
    errorMessage = string.formatString(
      string.MAXIMUM_HEIGHT_KEY,
      maxCustomHeight + unit
    );
  }

  const heightInImperial = valueInImperial(inValue, unitSystem);
  dispatch({
    type: actions.SELECT_CUSTOM_WALL_HEIGHT,
    payload: {
      value: heightInImperial,
      heightInput: heightInImperial,
      heightValid,
      heightErrorMessage: errorMessage
    }
  });
  dispatch({
    type: actions.CUSTOM_WALL_ERROR,
    payload: { errorMessage }
  });
  if (heightValid == '') {
    dispatch(validateHeightSizeAttributesAndContinue());
  }
};

export const canvasWallWidthChange = inValue => (dispatch, getState) => {
  const { unitSystem, customWall, multiCanvas } = getState().home;
  const stringToFloatValue = parseFloat(inValue);
  const widthInImperial = valueInImperial(stringToFloatValue, unitSystem);

  const validationDict = dispatch(validateCanvasWallWidth(widthInImperial));
  const {
    widthValid,
    widthErrorMessage,
    heightValid,
    heightErrorMessage,
    errorMessage
  } = validationDict;

  dispatch({
    type: actions.SET_CANVAS_WALL_WIDTH,
    payload: { canvasWallWidth: widthInImperial, widthInput: widthInImperial }
  });

  dispatch({
    type: actions.SET_CANVAS_WALL_WIDTH_VALID,
    payload: { valid: widthValid, widthErrorMessage }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_HEIGHT_VALID,
    payload: {
      valid: heightValid,
      heightErrorMessage
    }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_ERROR,
    payload: { errorMessage }
  });
  if (widthValid == '') {
    const { currentPageIndex, isPresetSizePage } = getState().pageController;
    if (
      getState().home.selectedDefaultSize == 'custom' ||
      (currentPageIndex === SIZE_SELECTION_PAGE && !isPresetSizePage)
    ) {
      const canvasWidthInCurrentUnit = convertImperialToGivenUnitSystem(
        customWall.customWidthInput,
        unitSystem
      );
      dispatch(customWidthChange(canvasWidthInCurrentUnit));
    } else {
      dispatch(columnChange(multiCanvas.columnInput));
    }
  }
};

export const canvasWallHeightChange = inValue => (dispatch, getState) => {
  const { unitSystem, customWall, multiCanvas } = getState().home;
  const stringToFloatValue = parseFloat(inValue);
  const heightInImperial = valueInImperial(stringToFloatValue, unitSystem);

  const validationDict = dispatch(validateCanvasWallHeight(heightInImperial));
  const {
    widthValid,
    widthErrorMessage,
    heightValid,
    heightErrorMessage,
    errorMessage
  } = validationDict;

  dispatch({
    type: actions.SET_CANVAS_WALL_HEIGHT,
    payload: {
      canvasWallHeight: heightInImperial,
      heightInput: heightInImperial
    }
  });

  dispatch({
    type: actions.SET_CANVAS_WALL_WIDTH_VALID,
    payload: {
      valid: widthValid,
      widthErrorMessage
    }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_HEIGHT_VALID,
    payload: { valid: heightValid, heightErrorMessage }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_ERROR,
    payload: { errorMessage }
  });

  if (heightValid == '') {
    const { currentPageIndex, isPresetSizePage } = getState().pageController;
    if (
      getState().home.selectedDefaultSize == 'custom' ||
      (currentPageIndex === SIZE_SELECTION_PAGE && !isPresetSizePage)
    ) {
      const canvasHeightInCurrentUnit = convertImperialToGivenUnitSystem(
        customWall.customHeightInput,
        unitSystem
      );
      dispatch(customHeightChange(canvasHeightInCurrentUnit));
    } else {
      dispatch(rowChange(multiCanvas.rowInput));
    }
  }
};

export const rowChange = val => dispatch => {
  let rowValid = '';
  let errorMessage = '';

  if (val === null) {
    rowValid = '';
    errorMessage = '';
  } else if (Number.isNaN(val) || val <= 0 || !Number.isInteger(Number(val))) {
    rowValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
  }
  dispatch({
    type: actions.SET_ROW,
    payload: { value: val, rowInput: val }
  });
  dispatch({
    type: actions.SET_ROW_VALID,
    payload: { rowValid, rowErrorMessage: errorMessage }
  });
  dispatch({
    type: actions.SET_MULTI_CANVAS_ERROR,
    payload: { errorMessage }
  });

  if (rowValid == '') {
    dispatch(validateHeightSizeAttributesAndContinue());
  }
};

export const columnChange = val => dispatch => {
  let columnValid = '';
  let errorMessage = '';
  if (val === null) {
    columnValid = '';
    errorMessage = '';
  } else if (Number.isNaN(val) || val <= 0 || !Number.isInteger(Number(val))) {
    columnValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
  }
  dispatch({
    type: actions.SET_COLUMN,
    payload: { value: val, columnInput: val }
  });
  dispatch({
    type: actions.SET_COLUMN_VALID,
    payload: { columnValid, colErrorMessage: errorMessage }
  });
  dispatch({
    type: actions.SET_MULTI_CANVAS_ERROR,
    payload: { errorMessage }
  });

  if (columnValid === '') {
    dispatch(validateWidthSizeAttributesAndContinue());
  }
};

export const validateWidthSizeAttributesAndContinue = () => (
  dispatch,
  getState
) => {
  const { unitSystem, selectedDefaultSize } = getState().home;
  let widthValid = '';
  let wallWidthValid = '';
  let columnValid = '';
  let widthErrorMessage = '';
  let wallWidthErrorMessage = '';
  let colErrorMessage = '';

  if (dispatch(checkIfCanvasApp())) {
    const { width } = dispatch(getSelectedProjectSize());
    const { canvasTileType } = getState().finalCheck.project;
    const { canvasWall, multiCanvas } = getState().home;
    if (width > canvasWall.canvasWallWidth) {
      widthValid = 'is-invalid';
      wallWidthValid = 'is-invalid';
      widthErrorMessage = string.ALERT_CANVAS_WIDTH_EXCEEDS_KEY;
      wallWidthErrorMessage =
        string.WALL_WIDTH_IS_LESS_THAN_CANVAS_WIDTH_ERROR_TEXT_KEY;
    }

    if (
      canvasTileType === CANVAS_CONSTRAINT_TILE_TYPE &&
      multiCanvas.columnInput
    ) {
      const actualProjectWidth = width * multiCanvas.columnInput;
      if (actualProjectWidth > canvasWall.canvasWallWidth) {
        columnValid = 'is-invalid';
        colErrorMessage = string.ALERT_CANVAS_WIDTH_EXCEEDS_KEY;
      }
    }
    if (canvasTileType === CANVAS_FREESTYLE_TILE_TYPE) {
      const { minCustomWidth } = getState().modal.productSizes.custom.sizes[
        unitSystem
      ];
      const minCustomWidthInImperial = valueInImperial(
        minCustomWidth,
        unitSystem
      );
      const newSingleProjectWidth = width / multiCanvas.columnInput;
      if (newSingleProjectWidth < minCustomWidthInImperial) {
        const validColumnCount = Math.floor(width / minCustomWidthInImperial);
        columnValid = 'is-invalid';
        widthValid = 'is-invalid';
        widthErrorMessage = string.formatString(
          string.MAXIMUM_COLUMNS_ERROR_MESSAGE_KEY,
          validColumnCount
        );
        colErrorMessage = string.formatString(
          string.MAXIMUM_COLUMNS_ERROR_MESSAGE_KEY,
          validColumnCount
        );
      }
    }
    dispatch({
      type: actions.SET_WIDTH_SIZE_VALIDATIONS,
      payload: {
        widthValid,
        widthErrorMessage,
        wallWidthValid,
        wallWidthErrorMessage,
        columnValid,
        colErrorMessage
      }
    });
  }
  const { currentPageIndex } = getState().pageController;
  if (
    widthValid == '' &&
    columnValid == '' &&
    currentPageIndex === DESIGN_PAGE
  ) {
    dispatch(settingsWallChange(selectedDefaultSize));
  }
};

export const validateHeightSizeAttributesAndContinue = () => (
  dispatch,
  getState
) => {
  const { unitSystem, selectedDefaultSize } = getState().home;
  let heightValid = '';
  let wallHeightValid = '';
  let rowValid = '';
  let heightErrorMessage = '';
  let wallHeightErrorMessage = '';
  let rowErrorMessage = '';

  if (dispatch(checkIfCanvasApp())) {
    const { height } = dispatch(getSelectedProjectSize());
    const { canvasTileType } = getState().finalCheck.project;
    const { canvasWall, multiCanvas } = getState().home;
    if (height > canvasWall.canvasWallHeight) {
      heightValid = 'is-invalid';
      wallHeightValid = 'is-invalid';
      heightErrorMessage = string.ALERT_CANVAS_HEIGHT_EXCEEDS_KEY;
      wallHeightErrorMessage =
        string.WALL_HEIGHT_IS_LESS_THAN_CANVAS_HEIGHT_ERROR_TEXT_KEY;
    }

    if (
      canvasTileType === CANVAS_CONSTRAINT_TILE_TYPE &&
      multiCanvas.rowInput
    ) {
      const actualProjectHeight = height * multiCanvas.rowInput;
      if (actualProjectHeight > canvasWall.canvasWallHeight) {
        rowValid = 'is-invalid';
        rowErrorMessage = string.ALERT_CANVAS_HEIGHT_EXCEEDS_KEY;
      }
    }
    if (canvasTileType === CANVAS_FREESTYLE_TILE_TYPE) {
      const { minCustomHeight } = getState().modal.productSizes.custom.sizes[
        unitSystem
      ];
      const minCustomHeightInImperial = valueInImperial(
        minCustomHeight,
        unitSystem
      );
      const newSingleProjectHeight = height / multiCanvas.rowInput;
      if (newSingleProjectHeight < minCustomHeightInImperial) {
        const validRowCount = Math.floor(height / minCustomHeightInImperial);
        rowValid = 'is-invalid';
        rowErrorMessage = string.formatString(
          string.MAXIMUM_ROWS_ERROR_MESSAGE_KEY,
          validRowCount
        );
        heightValid = 'is-invalid';
        heightErrorMessage = string.formatString(
          string.MAXIMUM_ROWS_ERROR_MESSAGE_KEY,
          validRowCount
        );
      }
    }
    dispatch({
      type: actions.SET_HEIGHT_SIZE_VALIDATIONS,
      payload: {
        heightValid,
        heightErrorMessage,
        wallHeightValid,
        wallHeightErrorMessage,
        rowValid,
        rowErrorMessage
      }
    });
  }
  const { currentPageIndex } = getState().pageController;
  if (heightValid == '' && rowValid == '' && currentPageIndex == DESIGN_PAGE) {
    dispatch(settingsWallChange(selectedDefaultSize));
  }
};

export const validateCanvasWallWidth = inWidth => (dispatch, getState) => {
  const { unitSystem, canvasWall } = getState().home;
  const {
    canvasWallHeight,
    canvasWallHeightValid,
    canvasWallWidthErrorMessage,
    canvasWallHeightErrorMessage
  } = canvasWall;

  const { width } = dispatch(getSelectedProjectSize());

  let heightValid = canvasWallHeightValid;
  let heightErrorMessage = canvasWallHeightErrorMessage;
  // If the error is bcz of wall size exceeds maximum then reset previous error state of another field
  if (canvasWallWidthErrorMessage == canvasWallHeightErrorMessage) {
    heightValid = '';
    heightErrorMessage = '';
  }

  let widthValid = '';
  let widthErrorMessage = '';
  let errorMessage = '';

  const stringToFloatWidth = parseFloat(inWidth);

  if (Number.isNaN(stringToFloatWidth) || stringToFloatWidth <= 0) {
    widthValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
    widthErrorMessage = errorMessage;
  } else if (stringToFloatWidth < parseFloat(width)) {
    widthValid = 'is-invalid';
    errorMessage = string.WALL_WIDTH_IS_LESS_THAN_CANVAS_WIDTH_ERROR_TEXT_KEY;
    widthErrorMessage = errorMessage;
  } else if (stringToFloatWidth < WALL_MINIMUM_TOTAL_WIDTH) {
    widthValid = 'is-invalid';
    const minimumWidth = convertImperialToGivenUnitSystem(
      WALL_MINIMUM_TOTAL_WIDTH,
      unitSystem
    );
    errorMessage = string.formatString(
      string.WALL_MINIMUM_WIDTH_ERROR_TEXT_KEY,
      minimumWidth,
      getUnit(unitSystem)
    );
    widthErrorMessage = errorMessage;
  } else {
    const wallSize = stringToFloatWidth * canvasWallHeight;
    if (wallSize > MAX_CANVAS_WALL_SIZE) {
      widthValid = 'is-invalid';
      heightValid = 'is-invalid';
      let unit = '';
      if (unitSystem === METRIC_KEY) {
        unit = '.m';
      } else {
        unit = '.ft';
      }
      const maximumSize = getAreaInGivenUnitSystem(
        MAX_CANVAS_WALL_SIZE,
        unitSystem
      );
      errorMessage = string.formatString(
        string.MAXIMUM_WALL_SIZE_KEY,
        maximumSize,
        unit
      );
      widthErrorMessage = errorMessage;
      heightErrorMessage = errorMessage;
    }
  }
  return {
    widthValid,
    widthErrorMessage,
    heightValid,
    heightErrorMessage,
    errorMessage
  };
};

export const validateCanvasWallHeight = inHeight => (dispatch, getState) => {
  const { unitSystem, canvasWall } = getState().home;
  const {
    canvasWallWidth,
    canvasWallWidthValid,
    canvasWallWidthErrorMessage,
    canvasWallHeightErrorMessage
  } = canvasWall;
  const { height } = dispatch(getSelectedProjectSize());

  let widthValid = canvasWallWidthValid;
  let widthErrorMessage = canvasWallWidthErrorMessage;
  // If the error is bcz of wall size exceeds maximum then reset previous error state of another field
  if (canvasWallWidthErrorMessage == canvasWallHeightErrorMessage) {
    widthValid = '';
    widthErrorMessage = '';
  }

  let heightValid = '';
  let heightErrorMessage = '';
  let errorMessage = '';

  const stringToFloatHeight = parseFloat(inHeight);
  if (Number.isNaN(stringToFloatHeight) || stringToFloatHeight <= 0) {
    heightValid = 'is-invalid';
    errorMessage = string.INVALID_NUMBER_ERROR_TEXT_KEY;
    heightErrorMessage = errorMessage;
  } else if (stringToFloatHeight < parseFloat(height)) {
    heightValid = 'is-invalid';
    errorMessage = string.WALL_HEIGHT_IS_LESS_THAN_CANVAS_HEIGHT_ERROR_TEXT_KEY;
    heightErrorMessage = errorMessage;
  } else if (stringToFloatHeight < WALL_MINIMUM_TOTAL_HEIGHT) {
    heightValid = 'is-invalid';
    const minimumHeight = convertImperialToGivenUnitSystem(
      WALL_MINIMUM_TOTAL_HEIGHT,
      unitSystem
    );
    errorMessage = string.formatString(
      string.WALL_MINIMUM_HEIGHT_ERROR_TEXT_KEY,
      minimumHeight,
      getUnit(unitSystem)
    );
    heightErrorMessage = errorMessage;
  } else if (stringToFloatHeight > WALL_MAXIMUM_TOTAL_HEIGHT) {
    heightValid = 'is-invalid';
    const maximumHeight = convertImperialToGivenUnitSystem(
      WALL_MAXIMUM_TOTAL_HEIGHT,
      unitSystem
    );
    errorMessage = string.formatString(
      string.MAXIMUM_WALL_HEIGHT_KEY,
      maximumHeight,
      getUnit(unitSystem)
    );
    heightErrorMessage = errorMessage;
  } else {
    const wallSize = stringToFloatHeight * canvasWallWidth;
    if (wallSize > MAX_CANVAS_WALL_SIZE) {
      widthValid = 'is-invalid';
      heightValid = 'is-invalid';
      let unit = '';
      if (unitSystem === METRIC_KEY) {
        unit = '.m';
      } else {
        unit = '.ft';
      }
      const maximumSize = getAreaInGivenUnitSystem(
        MAX_CANVAS_WALL_SIZE,
        unitSystem
      );
      errorMessage = string.formatString(
        string.MAXIMUM_WALL_SIZE_KEY,
        maximumSize,
        unit
      );
      widthErrorMessage = errorMessage;
      heightErrorMessage = errorMessage;
    }
  }
  return {
    widthValid,
    widthErrorMessage,
    heightValid,
    heightErrorMessage,
    errorMessage
  };
};

export const doesProjectSizeExceedsWallSize = (width, height) => (
  dispatch,
  getState
) => {
  const { canvasWallWidth, canvasWallHeight } = getState().home.canvasWall;
  let validProject = false;

  const projectWidthValid =
    Number.isNaN(canvasWallWidth) || width > canvasWallWidth
      ? 'is-invalid'
      : '';
  const projectHeightValid =
    Number.isNaN(canvasWallHeight) || height > canvasWallHeight
      ? 'is-invalid'
      : '';

  validProject = projectWidthValid === '' && projectHeightValid === '';

  return { isValid: validProject, projectWidthValid, projectHeightValid };
};

export const setSizeExceedValidationResult = (
  widthValid,
  heightValid
) => dispatch => {
  let errorMessage = '';
  let widthErrorMessage = '';
  let heightErrorMessage = '';

  if (widthValid != '') {
    widthErrorMessage =
      string.WALL_WIDTH_IS_LESS_THAN_CANVAS_WIDTH_ERROR_TEXT_KEY;
    errorMessage = widthErrorMessage;
  }
  if (heightValid != '') {
    heightErrorMessage =
      string.WALL_HEIGHT_IS_LESS_THAN_CANVAS_HEIGHT_ERROR_TEXT_KEY;
    errorMessage = heightErrorMessage;
  }
  dispatch({
    type: actions.SET_CANVAS_WALL_WIDTH_VALID,
    payload: { valid: widthValid, widthErrorMessage }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_HEIGHT_VALID,
    payload: { valid: heightValid, heightErrorMessage }
  });
  dispatch({
    type: actions.SET_CANVAS_WALL_ERROR,
    payload: { errorMessage }
  });

  if (widthValid == '' && heightValid == '') {
    dispatch(resetMultiCanvasErrorState());
  }
};

export const sizeSettingsAreChanged = (
  inProjectWidth,
  inProjectHeight,
  inRows,
  inCols,
  inWallWidth,
  inWallHeight
) => (dispatch, getState) => {
  const isCanvas = dispatch(checkIfCanvasApp());
  const activeWall = dispatch(getActiveWall());

  const { projectWidth, projectHeight } = getState().home;
  const oldProjectSize = { width: projectWidth, height: projectHeight };
  const { project } = getState().finalCheck;
  const { currentPageIndex } = getState().pageController;

  const oldMultiCanvasValues = {
    rows: project.canvasRowsCount,
    cols: project.canvasColumnsCount
  };
  const oldWallSize = {
    width: activeWall.width,
    height: activeWall.height
  };

  dispatch({
    type: actions.SET_PROJECT_SIZE,
    payload: {
      width: inProjectWidth,
      height: inProjectHeight
    }
  });
  project.canvasRowsCount = inRows;
  project.canvasColumnsCount = inCols;

  activeWall.width = inWallWidth;
  activeWall.height = inWallHeight;

  if (!isCanvas) {
    project.walls.forEach(inWall => {
      inWall.width = inWallWidth;
      inWall.height = inWallHeight;
    });
  }
  dispatch({
    type: actions.WALL_SIZE_CHANED_FLAG,
    payload: { value: true }
  });
  dispatch(updateUIForProjectSize(inProjectWidth, inProjectHeight));
  dispatch(updateCanvasForChangeInSize());
  dispatch(minimumViewingDistanceIsUpdated());
  if (currentPageIndex < DESIGN_PAGE || currentPageIndex == DESIGN_SHARE_PAGE) {
    dispatch(setCurrentPageIndex(DESIGN_PAGE));
  }

  dispatch(
    addToUndoManager({
      undo: () => {
        dispatch(
          sizeSettingsAreChanged(
            oldProjectSize.width,
            oldProjectSize.height,
            oldMultiCanvasValues.rows,
            oldMultiCanvasValues.cols,
            oldWallSize.width,
            oldWallSize.height
          )
        );
      },
      redo: () => {
        dispatch(
          sizeSettingsAreChanged(
            inProjectWidth,
            inProjectHeight,
            inRows,
            inCols,
            inWallWidth,
            inWallHeight
          )
        );
      }
    })
  );
};

export const getSelectedProjectSize = () => (dispatch, getState) => {
  const { selectedDefaultSize, customWall } = getState().home;
  if (selectedDefaultSize == 'custom') {
    return { width: customWall.customWidth, height: customWall.customHeight };
  }
  const { width, height } = getState().modal.productSizes.default.sizes[
    selectedDefaultSize
  ];
  return { width, height };
};

export const resetMultiCanvasErrorState = () => dispatch => {
  dispatch({
    type: actions.SET_ROW_VALID,
    payload: { rowValid: '', rowErrorMessage: '' }
  });
  dispatch({
    type: actions.SET_COLUMN_VALID,
    payload: { columnValid: '', colErrorMessage: '' }
  });
  dispatch({
    type: actions.SET_MULTI_CANVAS_ERROR,
    payload: { errorMessage: '' }
  });
};
